/**
 * Criteria that unlock personalized ("recommended") provider search.
 * Call sites must use getAiRecommendationCriteria / canUseAIRecommendations —
 * do not re-implement the location + age + services triple elsewhere.
 *
 * Services unlock is Required / must-have only:
 * - general: `required_core_services`
 * - community: `required_service_keys`
 * Either lane satisfies the essential-services criterion (unified Care Services
 * accordion). Preferred / nice-to-have lanes improve ranking but never unlock.
 *
 * locationValue:
 * - Form / promo: pass the live location draft (e.g. inputValue).
 * - Mode inference from persisted filters: pass filters.location_query
 *   (typically after trim / normalizeString).
 */

export type AiRecommendationCriterionId =
  | "location"
  | "age_groups"
  | "essential_services";

export type AiRecommendationCriterion = {
  id: AiRecommendationCriterionId;
  label: string;
  met: boolean;
};

export type AiRecommendationCriteriaProgress = {
  criteria: AiRecommendationCriterion[];
  metCount: number;
  total: number;
  remaining: AiRecommendationCriterion[];
};

export type AiRecommendationCriteriaInput = {
  locationValue: string;
  ageGroupsServed?: string[] | null;
  /** General Care Services must-have lane (`required_core_services`). */
  requiredCoreServices?: string[] | null;
  /** Community Care Services must-have lane (`required_service_keys`). */
  requiredServiceKeys?: string[] | null;
};

/** True when either Required / must-have Care Services lane has a selection. */
export function hasRequiredCareServicesForAiUnlock(
  options: Pick<
    AiRecommendationCriteriaInput,
    "requiredCoreServices" | "requiredServiceKeys"
  >
): boolean {
  const hasMeaningful = (items?: string[] | null): boolean =>
    Array.isArray(items) &&
    items.some((item) => typeof item === "string" && item.trim().length > 0);

  return (
    hasMeaningful(options.requiredCoreServices) ||
    hasMeaningful(options.requiredServiceKeys)
  );
}

export function getAiRecommendationCriteria(
  options: AiRecommendationCriteriaInput
): AiRecommendationCriteriaProgress {
  const criteria: AiRecommendationCriterion[] = [
    {
      id: "location",
      label: "Location (ZIP or Address)",
      met: options.locationValue.trim().length > 0,
    },
    {
      id: "age_groups",
      label: "Age Groups Served",
      met: (options.ageGroupsServed?.length ?? 0) > 0,
    },
    {
      id: "essential_services",
      label: "Required Care Services (Must Have)",
      met: hasRequiredCareServicesForAiUnlock(options),
    },
  ];

  const remaining = criteria.filter((criterion) => !criterion.met);
  return {
    criteria,
    metCount: criteria.length - remaining.length,
    total: criteria.length,
    remaining,
  };
}

/** True when all unlock criteria are met (personalized recommendations eligible). */
export function canUseAIRecommendations(
  options: AiRecommendationCriteriaInput
): boolean {
  return getAiRecommendationCriteria(options).remaining.length === 0;
}
